import React, { FC } from 'react';
import * as api from '../api';
import { useValidatedFormValue } from '../controllers/form.controller';
import { useFlags } from '../feature-flags/main';
import logger from '../logging';
import { Plan } from '../models';

const log = logger(__filename);

export type SaveStatus = 'noSave:pristine' | 'noSave:invalid' | 'canSave' | 'canSave:hasWarning' | 'saving';

export interface ChildProps {
  saveStatus: SaveStatus;
  onSave?: () => void;
}

interface SaveMemberGroupQueryProps {
  plans: Plan[];
  isNew: boolean;
  isDirty?: boolean;
  onSaveComplete?: (memberGroupId: string) => void | Promise<void>;
  onSaveError?: (error: any) => void;
  children: (props: ChildProps) => React.ReactElement | null;
}

export const SaveMemberGroupQuery: FC<SaveMemberGroupQueryProps> = ({
  plans,
  isNew,
  isDirty,
  onSaveComplete,
  onSaveError,
  children,
}) => {
  const { 'membership-single-source-of-truth-in-eligibility': membershipSingleSourceOfTruthInEligibility } = useFlags();
  const formValue = useValidatedFormValue();
  const [isSaving, setIsSaving] = React.useState<boolean>();

  let saveStatus: SaveStatus;
  if (isSaving) saveStatus = 'saving';
  else if (!formValue.isValid) saveStatus = 'noSave:invalid';
  else if (!isDirty) saveStatus = 'noSave:pristine';
  else if (formValue.hasWarning) saveStatus = 'canSave:hasWarning';
  else saveStatus = 'canSave';

  if (membershipSingleSourceOfTruthInEligibility) {
    return children({
      saveStatus,
      onSave() {
        if (!formValue.isValid) {
          log.warn('Cannot save member group, value is invalid');
          return;
        }
        setIsSaving(true);
        if (isNew) {
          api
            .saveNewMemberGroup(formValue.value, plans)
            .then(
              result => {
                if (!result) {
                  return onSaveError?.({
                    error: 'Created member group but could not get location from response header',
                  });
                }
                log.debug({ memberGroupId: formValue.value.id }, 'Created member group successfully');
                const splitUrl = result.split('/');
                const memberGroupId = splitUrl[splitUrl.length - 1];
                return onSaveComplete?.(memberGroupId);
              },
              err => {
                log.error({ err }, 'Failed to create member group');
                return onSaveError?.(err);
              },
            )
            .finally(() => setIsSaving(false));
        } else {
          api
            .saveExistingMemberGroup(formValue.value, plans)
            .then(
              result => {
                log.debug({ memberGroupId: formValue.value.id }, 'Updated member group successfully');
                return onSaveComplete?.(result);
              },
              err => {
                log.error({ err }, 'Failed to update member group');
                return onSaveError?.(err);
              },
            )
            .finally(() => setIsSaving(false));
        }
      },
    });
  }

  return children({
    saveStatus,
    onSave() {
      if (!formValue.isValid) {
        log.warn('Cannot save member group, value is invalid');
        return;
      }

      setIsSaving(true);
      api
        .saveMemberGroup(formValue.value)
        .then(
          result => {
            log.debug({ memberGroupId: formValue.value.id }, 'Saved member group successfully');
            return onSaveComplete?.(result[0].external_employee_id);
          },
          err => {
            log.error({ err }, 'Failed to save member group');
            return onSaveError?.(err);
          },
        )
        .finally(() => setIsSaving(false));
    },
  });
};
